// CommentTool.jsx
// Description: This file contains the implementation of the CommentTool class. This class is responsible for rendering the CommentTool component and handling the logic for the CommentTool.

// Copyright HS Analysis GmbH, 2024
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// Mui imports
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CommentIcon from "@mui/icons-material/Comment";
import Crop169Icon from "@mui/icons-material/Crop169";
import DeleteIcon from "@mui/icons-material/Delete";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// import icons
import { faDrawPolygon, faRuler } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// HSA imports
import { CommentROI } from "../../utils/ROI";
import { distance } from "../../utils/PolygonUtil";
import LazyRender from "../../../common/components/LazyRender";
import SketchColorPicker from "../../../common/components/SketchColorPicker";
import Tool from "./Tool";

const scaleUnitLabels = [
  "km",
  "m",
  "mm",
  "\u00B5m",
  "nm",
  "pm",
  "fm",
  "am",
  "zm",
];
const scaleUnits = [3, 1, -3, -6, -9, -12, -15, -18, -21];

/**
 * Enum-like object for comment types used in the viewer tool.
 * @readonly
 * @enum {string}
 * @property {string} RECTANGLE   - Represents a rectangular comment area.
 * @property {string} ARROW       - Represents an arrow annotation.
 * @property {string} REGION      - Represents a region selection.
 * @property {string} DISTANCE    - Represents a distance measurement.
 * @property {string} COMMENT_BOX - Represents a comment box.
 * @property {string} NONE        - Represents no comment type selected.
 */
export const CommentType = Object.freeze({
  RECTANGLE: "rectangle",
  ARROW: "arrow",
  REGION: "region",
  DISTANCE: "distance",
  COMMENT_BOX: "commentBox",
  NONE: "none",
});

class CommentTool extends Tool {
  constructor() {
    super();
    this.name = "Comment";
    this.noConfig = false;
    this.mouseActionState = "free";
    this.downScale = 2;
    this.points = [];
    this.commentState = {
      commentValue: "",
      color: "#D0021B",
      selectedTool: CommentType.NONE,
    };
    this.selectedCommentIdx = 0;
    this.clickedCommentIdx = 0;
    this.lastP = null;
    this.gripData = null;
  }

  setLayer(obj) {
    // Be resilient if ome/channels are not yet loaded
    this.ome = obj?.ome || {};
    const channels = this.ome?.channels;
    this.isBrightfield =
      Array.isArray(channels) && channels[0]?.type === "brightfield";
    this.lineColor = this.isBrightfield ? "#000000" : "#ffffff";
    this.selectionColor = this.lineColor;

    this.commentLayer =
      obj?.commentLayer && Array.isArray(obj.commentLayer.commentRois)
        ? obj.commentLayer
        : { commentRois: [] };
  }

  // mark comments as edited and
  updateCommentSideBar() {
    this.commentLayer.edited = true;
  }

  setPreviewRect() {}

  distance(p1, p2) {
    let deltaX = p1.x - p2.x;
    let deltaY = p1.y - p2.y;
    return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
  }

  generateDistancePoints(p1, p2) {
    let vector = {
      x: p1.x - p2.x,
      y: p1.y - p2.y,
    };
    let length = Math.sqrt(vector.x * vector.x + vector.y * vector.y);
    let dl = length / 20;
    vector.x /= length;
    vector.y /= length;
    return [
      [p1.x, p1.y],
      [p2.x, p2.y],
      [p2.x - (dl * vector.y) / 2, p2.y + (dl * vector.x) / 2],
      [p2.x + (dl * vector.y) / 2, p2.y - (dl * vector.x) / 2],
      [p2.x, p2.y],
      [p1.x, p1.y],
      [p1.x - (dl * vector.y) / 2, p1.y + (dl * vector.x) / 2],
      [p1.x + (dl * vector.y) / 2, p1.y - (dl * vector.x) / 2],
    ];
  }

  generateArrowPoints(p1, p2) {
    let vector = {
      x: p1.x - p2.x,
      y: p1.y - p2.y,
    };
    let length = Math.sqrt(vector.x * vector.x + vector.y * vector.y);
    let dl = length / 20;
    vector.x /= length;
    vector.y /= length;
    return [
      [p1.x, p1.y],
      [p2.x + vector.x * dl, p2.y + vector.y * dl],
      [
        p2.x + vector.x * dl - (dl * vector.y) / 2,
        p2.y + vector.y * dl + (dl * vector.x) / 2,
      ],
      [p2.x, p2.y],
      [
        p2.x + vector.x * dl + (dl * vector.y) / 2,
        p2.y + vector.y * dl - (dl * vector.x) / 2,
      ],
      [p2.x + vector.x * dl, p2.y + vector.y * dl],
    ];
  }

  deleteCommentRoi(idx) {
    this.commentLayer.commentRois.splice(idx, 1);
    this.updateCommentSideBar();
  }

  changeSelectedColor(idx, color) {
    this.commentLayer.commentRois[idx].color = color;
    this.updateCommentSideBar();
  }

  changeSelectedCommentValue(idx, commentValue) {
    this.commentLayer.commentRois[idx].commentValue = commentValue;
    this.updateCommentSideBar();
  }

  changeSelectedCommentIdx(idx) {
    // provides bad user-experience when using for documentation purposes
    // window.zoomToRect(this.commentLayer.commentRois[idx].bounds);
    this.selectedCommentIdx = idx;
    const selectedCommentRoi = this.commentLayer["commentRois"][idx];
    if (!selectedCommentRoi) return;
    window.zoomToRect(selectedCommentRoi.bounds);
    window.forceSidebarUpdate();
  }

  /**
   * Updates the index of the last clicked comment to match the currently selected comment index.
   * Also triggers a sidebar update by calling the global `window.forceSidebarUpdate` function.
   */
  updateClickedCommentIdx() {
    this.clickedCommentIdx = this.selectedCommentIdx;
    window.forceSidebarUpdate();
  }

  computePolygonArea(points) {
    let X = [];
    let Y = [];
    let numPoints = points.length;
    for (let p of points) {
      X.push(p[0]);
      Y.push(p[1]);
    }
    let area = 0; // Accumulates area
    let j = numPoints - 1;

    for (let i = 0; i < numPoints; i++) {
      area += (X[j] + X[i]) * (Y[j] - Y[i]);
      j = i; //j is previous vertex to i
    }
    return area / 2;
  }

  computLengthValue(physicalLength, area) {
    physicalLength = Math.abs(physicalLength);
    let computedValue = 0;
    // display pixel if no unit is set
    if (!this.ome.physicalSizeX) {
      return Math.round(physicalLength) + " px";
    }
    let unitLength = physicalLength;

    // find best fitting exponennt
    let unitExponent = 1;
    let unitExponentIndex = 1;
    for (
      unitExponentIndex = 0;
      unitExponentIndex < scaleUnits.length;
      unitExponentIndex++
    ) {
      unitExponent = area
        ? scaleUnits[unitExponentIndex] * 2
        : scaleUnits[unitExponentIndex];
      unitLength = physicalLength / Math.pow(10, unitExponent);
      if (unitLength > 1) {
        break;
      }
    }
    if (area) {
      computedValue =
        Math.round(unitLength * 100) / 100 +
        " " +
        scaleUnitLabels[unitExponentIndex] +
        "²";
    } else {
      computedValue =
        Math.round(unitLength * 100) / 100 +
        " " +
        scaleUnitLabels[unitExponentIndex];
    }
    return computedValue;
  }

  pointInsideBounds(p, bounds) {
    return !(
      p.x < bounds.left ||
      p.x > bounds.right ||
      p.y < bounds.top ||
      p.y > bounds.bottom
    );
  }

  addPoint(event, p) {
    let eventPoint = { x: event.clientX, y: event.clientY };
    if (this.points.length < 3) {
      this.points.push([p.x, p.y]);
      this.lastEventPoint = eventPoint;
    } else {
      const dist = distance(this.lastEventPoint, eventPoint);
      if (dist < 10) {
        this.points[this.points.length - 1] = [p.x, p.y];
      } else {
        this.points.push([p.x, p.y]);
        this.lastEventPoint = eventPoint;
      }
    }
  }

  /**
   * Computes the required width for a comment box including padding
   * @param {string} text - The text content
   * @param {CanvasRenderingContext2D} ctx - Canvas context for text measurement
   * @param {number} fontSize - Font size in pixels
   * @returns {number} Total width required for the comment box
   */
  computeCommentBoxWidth(text, ctx, fontSize) {
    if (!text) return 50; // minimum width for empty text

    const padding = fontSize * 0.5;
    ctx.font = `${fontSize}px Arial`;

    // Split into lines and find widest
    const lines = text.split("\n");
    const maxLineWidth = lines.reduce((maxWidth, line) => {
      const metrics = ctx.measureText(line);
      return Math.max(maxWidth, metrics.width);
    }, 0);

    // Return width with padding on both sides
    return maxLineWidth + padding * 2;
  }

  /**
   * Handles resizing of rectangular and region comments
   * @param {Object} selectedCommentRoi - The selected comment ROI
   * @param {number} deltaX - X-axis movement
   * @param {number} deltaY - Y-axis movement
   */
  handleRectangleResize(selectedCommentRoi, deltaX, deltaY) {
    // Calculate new dimensions
    const newWidth = Math.max(selectedCommentRoi.width + deltaX, 2);
    const newHeight = Math.max(selectedCommentRoi.height + deltaY, 2);

    // Calculate scale factors based on new dimensions
    let deltaXProc = newWidth / selectedCommentRoi.width;
    let deltaYProc = newHeight / selectedCommentRoi.height;

    let regions = [];
    for (let region of selectedCommentRoi.regions) {
      let x = selectedCommentRoi.bounds.left;
      let y = selectedCommentRoi.bounds.top;
      let w = region[0] - x;
      let h = region[1] - y;
      regions.push([x + w * deltaXProc, y + h * deltaYProc]);
    }

    this.commentLayer["commentRois"][this.selectedCommentIdx].regions = regions;
    this.commentLayer["commentRois"][this.selectedCommentIdx].updateBounds();

    let length = this.computePolygonArea(selectedCommentRoi.regions);
    let unitLength = length * this.ome.physicalSizeX * this.ome.physicalSizeY;
    if (!this.ome.physicalSizeX) {
      unitLength = length;
    }

    let computedValue = this.computLengthValue(unitLength, true);
    this.commentLayer["commentRois"][this.selectedCommentIdx].computedValue =
      computedValue;

    if (
      this.commentLayer["commentRois"][
        this.selectedCommentIdx
      ].commentValue.endsWith("m²")
    ) {
      this.commentLayer["commentRois"][this.selectedCommentIdx].commentValue =
        computedValue;
    }
  }

  /**
   * Handles moving of comments
   * @param {Object} mouseP - Mouse position
   * @param {Object} selectedCommentRoi - The selected comment ROI
   * @param {number} deltaX - X-axis movement
   * @param {number} deltaY - Y-axis movement
   */
  handleMove(mouseP, selectedCommentRoi, deltaX, deltaY) {
    const regions = [];
    for (let region of selectedCommentRoi.regions) {
      regions.push([region[0] + deltaX, region[1] + deltaY]);
    }
    this.commentLayer["commentRois"][this.selectedCommentIdx].regions = regions;
    this.commentLayer["commentRois"][this.selectedCommentIdx].updateBounds();
    this.lastP = mouseP;
  }

  /**
   * Handles distance/arrow comment resizing
   * @param {Object} mouseP - Mouse position
   * @param {Object} selectedCommentRoi - The selected comment ROI
   * @param {number} deltaX - X-axis movement
   * @param {number} deltaY - Y-axis movement
   */
  handleDistanceResize(mouseP, selectedCommentRoi, deltaX, deltaY) {
    let regions = selectedCommentRoi.regions;
    let p1 = { x: regions[0][0], y: regions[0][1] };
    let p2 = { x: regions[1][0], y: regions[1][1] };

    if (selectedCommentRoi.type === CommentType.ARROW) {
      p2.x = regions[3][0];
      p2.y = regions[3][1];
    }

    if (this.distance(mouseP, p1) < this.distance(mouseP, p2)) {
      p1.x += deltaX;
      p1.y += deltaY;
    } else {
      p2.x += deltaX;
      p2.y += deltaY;
    }

    regions =
      selectedCommentRoi.type === CommentType.ARROW
        ? this.generateArrowPoints(p1, p2)
        : this.generateDistancePoints(p1, p2);

    let length = this.distance(p1, p2);
    let computedValue = !this.ome.physicalSizeX
      ? this.computLengthValue(length, false)
      : this.computLengthValue(this.ome.physicalSizeX * length, false);

    let commentValue = selectedCommentRoi.commentValue;
    if (commentValue.endsWith("m")) {
      commentValue = computedValue;
    }

    this.commentLayer["commentRois"][this.selectedCommentIdx] = new CommentROI(
      regions,
      selectedCommentRoi.color,
      selectedCommentRoi.type,
      commentValue,
      computedValue
    );
  }

  /**
   * Initializes a new comment ROI
   * @param {Object} mouseP - Mouse position
   * @param {Object} event - Mouse event
   * @param {string} computedValue - Computed measurement value
   */
  handleNewComment(mouseP, event, computedValue) {
    this.startPoint = mouseP;
    this.points = [];
    this.addPoint(event, mouseP);

    const newComment = new CommentROI(
      this.points,
      this.commentState.color,
      this.commentState.selectedTool,
      this.commentState.commentValue,
      computedValue
    );

    if (this.commentLayer.commentRois) {
      this.commentLayer.commentRois.push(newComment);
      this.selectedCommentIdx = this.commentLayer["commentRois"].length - 1;
      this.clickedCommentIdx = this.selectedCommentIdx;
    } else {
      this.commentLayer["commentRois"] = [newComment];
    }

    this.updateCommentSideBar();
  }

  // triggered by Renderer
  mouse(params) {
    let { event, p } = params;
    let mouseP = p;
    if (!this.lastP) {
      this.lastP = mouseP;
    }
    let computedValue = "";
    let commentValue = this.commentState.commentValue;

    // Early return if not left mouse button
    if (event.button !== 0) return;

    // Handle existing comment interactions
    const hasExistingComments = this.commentLayer?.commentRois?.length > 0;
    if (this.mouseActionState !== "draw" && hasExistingComments) {
      let selectedCommentRoi =
        this.commentLayer["commentRois"][this.selectedCommentIdx];

      // Handle free mode selection
      if (
        this.mouseActionState === "free" &&
        this.commentState.selectedTool === CommentType.NONE
      ) {
        let minArea = Number.MAX_SAFE_INTEGER;
        for (let i = 0; i < this.commentLayer.commentRois.length; i++) {
          if (this.commentLayer.commentRois[i].containsPoint(mouseP)) {
            let commentRoiArea =
              this.commentLayer.commentRois[i].width *
              this.commentLayer.commentRois[i].height;
            if (commentRoiArea < minArea) {
              this.selectedCommentIdx = i;
              minArea = commentRoiArea;
            }
          }
        }
      } else {
        // Handle mouse events for selected comment
        if (event.type === "mouseup" || event.type === "mouseleave") {
          this.mouseActionState = "free";
        } else if (event.type === "mousemove") {
          const deltaX = mouseP.x - this.lastP.x;
          const deltaY = mouseP.y - this.lastP.y;

          if (this.mouseActionState === "move") {
            this.handleMove(mouseP, selectedCommentRoi, deltaX, deltaY);
          } else if (this.mouseActionState === "resize" && selectedCommentRoi) {
            if (
              selectedCommentRoi.type === CommentType.RECTANGLE ||
              selectedCommentRoi.type === CommentType.REGION ||
              selectedCommentRoi.type === CommentType.COMMENT_BOX
            ) {
              this.handleRectangleResize(selectedCommentRoi, deltaX, deltaY);
            } else if (
              selectedCommentRoi.type === CommentType.DISTANCE ||
              selectedCommentRoi.type === CommentType.ARROW
            ) {
              this.handleDistanceResize(
                mouseP,
                selectedCommentRoi,
                deltaX,
                deltaY
              );
            }
            this.lastP = mouseP;
          }
        }
      }

      // Handle mousedown on existing comment
      if (event.type === "mousedown") {
        if (
          this.gripData !== null &&
          this.pointInsideBounds(mouseP, this.gripData.b)
        ) {
          this.mouseActionState = "resize";
        } else if (selectedCommentRoi?.containsPoint(mouseP)) {
          this.mouseActionState = "move";
        } else {
          this.mouseActionState = "draw";
        }
        this.lastP = mouseP;
      }
    }

    // Handle drawing new comments
    if (event.type === "mousedown") {
      if (
        this.commentState.selectedTool !== CommentType.NONE &&
        (!this.commentLayer["commentRois"] ||
          !this.commentLayer["commentRois"][this.selectedCommentIdx] ||
          ((!this.gripData?.b ||
            !this.pointInsideBounds(mouseP, this.gripData.b)) &&
            !this.commentLayer["commentRois"][
              this.selectedCommentIdx
            ]?.containsPoint(mouseP)))
      ) {
        this.mouseActionState = "draw";
        this.handleNewComment(mouseP, event, computedValue);
      } else if (
        hasExistingComments &&
        this.commentLayer["commentRois"][this.selectedCommentIdx].containsPoint(
          mouseP
        )
      ) {
        this.updateClickedCommentIdx();
      }
    } else if (event.type === "mouseup") {
      let lastRoi =
        this.commentLayer.commentRois[this.commentLayer.commentRois.length - 1];
      if (!lastRoi) {
        console.log("LAST ROI WAS EMPTY");
      } else if (
        lastRoi.regions.length < 4 ||
        lastRoi.width === 0 ||
        lastRoi.height === 0
      ) {
        console.info("Comment could not be created!");
        this.commentLayer.commentRois.pop();
        this.updateCommentSideBar();
      }
      this.mouseActionState = "free";
    } else if (event.type === "mousemove" && this.mouseActionState === "draw") {
      if (
        this.commentState.selectedTool === CommentType.RECTANGLE ||
        this.commentState.selectedTool === CommentType.COMMENT_BOX
      ) {
        this.points = [
          [this.startPoint.x, this.startPoint.y],
          [mouseP.x, this.startPoint.y],
          [mouseP.x, mouseP.y],
          [this.startPoint.x, mouseP.y],
        ];
        const width =
          Math.abs(this.startPoint.x - mouseP.x) *
          (this.ome.physicalSizeX || 1);
        const height =
          Math.abs(this.startPoint.y - mouseP.y) *
          (this.ome.physicalSizeY || 1);
        let unitLength = width * height;
        computedValue = this.computLengthValue(unitLength, true);
        if (
          this.commentState.commentValue === "" &&
          this.commentState.selectedTool !== CommentType.COMMENT_BOX
        ) {
          commentValue = computedValue;
        }
      } else if (this.commentState.selectedTool === CommentType.REGION) {
        this.addPoint(event, mouseP);
        let length = this.computePolygonArea(this.points);
        let unitLength =
          length *
          (this.ome.physicalSizeX || 1) *
          (this.ome.physicalSizeY || 1);
        computedValue = this.computLengthValue(unitLength, true);
        if (
          this.commentState.commentValue === "" &&
          this.commentState.selectedTool !== CommentType.COMMENT_BOX
        ) {
          commentValue = computedValue;
        }
      } else if (
        this.commentState.selectedTool === CommentType.ARROW ||
        this.commentState.selectedTool === CommentType.DISTANCE
      ) {
        let vector = {
          x: this.startPoint.x - mouseP.x,
          y: this.startPoint.y - mouseP.y,
        };
        let length = Math.sqrt(vector.x * vector.x + vector.y * vector.y);
        this.points =
          this.commentState.selectedTool === CommentType.ARROW
            ? this.generateArrowPoints(this.startPoint, mouseP)
            : this.generateDistancePoints(this.startPoint, mouseP);

        computedValue = this.computLengthValue(
          this.ome.physicalSizeX ? this.ome.physicalSizeX * length : length,
          false
        );

        if (
          this.commentState.commentValue === "" &&
          this.commentState.selectedTool === CommentType.DISTANCE
        ) {
          commentValue = computedValue;
        }
      } else {
        return;
      }

      // Update the current comment ROI
      this.commentLayer["commentRois"][
        this.commentLayer["commentRois"].length - 1
      ] = new CommentROI(
        this.points,
        this.commentState.color,
        this.commentState.selectedTool,
        commentValue,
        computedValue
      );
      this.updateCommentSideBar();
    }
  }

  /**
   * Draws grip points for the selected comment ROI
   * @param {CanvasRenderingContext2D} ctx - Canvas context
   * @param {Object} gripPoint - Grip point coordinates
   * @param {number} fct - Scale factor
   * @returns {Object} Grip data object
   */
  drawGripPoint(ctx, gripPoint, fct) {
    let gripSize = 14 / fct;
    let gripSizeInner = 10 / fct;
    let gripData = {
      p: {
        x: gripPoint.x,
        y: gripPoint.y,
      },
      b: {
        left: gripPoint.x - gripSize / 2,
        right: gripPoint.x + gripSize / 2,
        top: gripPoint.y - gripSize / 2,
        bottom: gripPoint.y + gripSize / 2,
      },
      bInner: {
        left: gripPoint.x - gripSizeInner / 2,
        right: gripPoint.x + gripSizeInner / 2,
        top: gripPoint.y - gripSizeInner / 2,
        bottom: gripPoint.y + gripSizeInner / 2,
      },
    };

    ctx.fillStyle = "black";
    ctx.fillRect(gripData.b.left, gripData.b.top, gripSize, gripSize);
    ctx.fillStyle = "white";
    ctx.fillRect(
      gripData.bInner.left,
      gripData.bInner.top,
      gripSizeInner,
      gripSizeInner
    );

    return gripData;
  }

  /**
   * Gets grip points based on comment ROI type
   * @param {Object} selectedCommentRoi - Selected comment ROI
   * @param {Object} bounds - Bounds object
   * @returns {Array} Array of grip points
   */
  getGripPoints(selectedCommentRoi, bounds) {
    if (
      selectedCommentRoi.type === CommentType.RECTANGLE ||
      selectedCommentRoi.type === CommentType.REGION ||
      selectedCommentRoi.type === CommentType.COMMENT_BOX
    ) {
      return [
        {
          x: bounds.right,
          y: bounds.bottom,
        },
      ];
    } else if (selectedCommentRoi.type === CommentType.DISTANCE) {
      return [
        {
          x: selectedCommentRoi.regions[0][0],
          y: selectedCommentRoi.regions[0][1],
        },
        {
          x: selectedCommentRoi.regions[1][0],
          y: selectedCommentRoi.regions[1][1],
        },
      ];
    } else if (selectedCommentRoi.type === CommentType.ARROW) {
      return [
        {
          x: selectedCommentRoi.regions[0][0],
          y: selectedCommentRoi.regions[0][1],
        },
        {
          x: selectedCommentRoi.regions[3][0],
          y: selectedCommentRoi.regions[3][1],
        },
      ];
    }
    return [];
  }

  drawCustomCursor(ctx, mousePosition, fct, canvas) {
    if (
      this.commentLayer["commentRois"].length > 0 &&
      this.commentLayer["commentRois"][this.selectedCommentIdx]
    ) {
      ctx.beginPath();
      ctx.globalAlpha = 1.0;
      let selectedCommentRoi =
        this.commentLayer["commentRois"][this.selectedCommentIdx];

      if (selectedCommentRoi.regions.length < 2) {
        return;
      }

      let pointInsideSelection =
        selectedCommentRoi.containsPoint(mousePosition);

      ctx.strokeStyle = pointInsideSelection ? "red" : this.selectionColor;
      let b = selectedCommentRoi.bounds;
      ctx.rect(b.left, b.top, b.right - b.left, b.bottom - b.top);
      ctx.stroke();
      ctx.closePath();

      let cursor = pointInsideSelection ? "move" : "default";
      let gripPoints = this.getGripPoints(selectedCommentRoi, b);

      for (let gripPoint of gripPoints) {
        this.gripData = this.drawGripPoint(ctx, gripPoint, fct);

        if (this.pointInsideBounds(mousePosition, this.gripData.b)) {
          cursor =
            selectedCommentRoi.type === CommentType.ARROW ||
            selectedCommentRoi.type === CommentType.DISTANCE
              ? "pointer"
              : "nw-resize";
          break;
        }
      }

      canvas.style.cursor = cursor;
    }
  }

  updateCommentRoi() {
    let computedValue = 0;
    if (this.commentLayer["commentRois"].length > 0) {
      this.commentLayer["commentRois"][this.selectedCommentIdx] =
        new CommentROI(
          this.commentLayer["commentRois"][this.selectedCommentIdx].regions,
          this.commentState.color,
          this.commentState.selectedTool,
          this.commentState.commentValue,
          computedValue
        );
    }
  }

  exit() {}

  renderConfiguration() {
    return (
      <div>
        <Typography variant="h6">{this.name}:</Typography>
        <ConfigForm
          selectedCommentIdx={this.selectedCommentIdx}
          clickedCommentIdx={this.clickedCommentIdx}
          changeSelectedCommentIdx={(idx) => {
            this.changeSelectedCommentIdx(idx);
          }}
          commentRois={this.commentLayer["commentRois"]}
          commentState={this.commentState}
          changeCommentState={(commentState) => {
            this.commentState = commentState;
            this.updateCommentSideBar();
          }}
          updateCommentRoi={() => {
            this.updateCommentRoi(this.commentState);
          }}
          deleteCommentRoi={(idx) => {
            this.deleteCommentRoi(idx);
          }}
          changeSelectedColor={(idx, color) => {
            this.changeSelectedColor(idx, color);
          }}
          changeSelectedCommentValue={(idx, commentValue) => {
            this.changeSelectedCommentValue(idx, commentValue);
          }}
        />
      </div>
    );
  }
}

const ConfigForm = (props) => {
  const {
    commentRois,
    selectedCommentIdx,
    clickedCommentIdx,
    changeSelectedCommentIdx,
    changeSelectedColor,
    deleteCommentRoi,
    changeSelectedCommentValue,
    changeCommentState,
  } = props;
  const forceUpdate = React.useReducer(() => ({}), [])[1];
  const [commentState, setCommentState] = React.useState(props.commentState);
  const listItemRefs = React.useRef({});
  const lazyRenderRef = React.useRef();

  React.useEffect(() => {
    if (commentRois.length < clickedCommentIdx) return;
    // Use the new scrollToIndex method
    if (lazyRenderRef.current && clickedCommentIdx !== undefined) {
      // Since the list is reversed, we need to calculate the correct index
      const reversedIndex = commentRois.length - 1 - clickedCommentIdx;
      lazyRenderRef.current.scrollToIndex(reversedIndex);
    }
  }, [clickedCommentIdx]);

  const updateCommentState = (newState) => {
    setCommentState(newState);
    changeCommentState(newState);
  };
  const changeCommentType = (type) => {
    const updatedCommentState = {
      ...commentState,
      selectedTool:
        commentState.selectedTool === type ? CommentType.NONE : type,
      commentValue: "",
    };
    updateCommentState(updatedCommentState);
  };

  const { commentValue, color, selectedTool } = commentState;
  return (
    <div style={{ height: "calc(100% - 32px)", overflowY: "hidden" }}>
      <div style={{ height: "190px" }}>
        <div style={{ display: "flex", marginTop: 20 }}>
          <TextField
            style={{ width: "300px", marginRight: "20px" }}
            name="Textfield"
            value={commentValue}
            size="small"
            label="Default value"
            placeholder="Enter default value..."
            variant="outlined"
            onChange={(e) => {
              const updatedCommentState = {
                ...commentState,
                commentValue: e.target.value,
              };
              updateCommentState(updatedCommentState);
            }}
          />
          <SketchColorPicker
            color={color}
            handleChange={(newColor) => {
              const updatedCommentState = { ...commentState, color: newColor };
              updateCommentState(updatedCommentState);
            }}
          />
        </div>
        <br />
        <div>
          <Button
            width="150px"
            style={{ marginRight: 10, marginBottom: 10, minWidth: 195 }}
            variant="contained"
            onClick={() => changeCommentType(CommentType.RECTANGLE)}
            color={
              selectedTool === CommentType.RECTANGLE ? "primary" : "default"
            }
            startIcon={<Crop169Icon />}
          >
            Rectangle
          </Button>
          <Button
            width="150px"
            style={{ marginRight: 10, marginBottom: 10, minWidth: 195 }}
            variant="contained"
            onClick={() => changeCommentType(CommentType.ARROW)}
            color={selectedTool === CommentType.ARROW ? "primary" : "default"}
            startIcon={<ArrowForwardOutlinedIcon />}
          >
            Arrow
          </Button>
        </div>
        <div>
          <Button
            width="150px"
            style={{ marginRight: 10, marginBottom: 10, minWidth: 195 }}
            variant="contained"
            onClick={() => changeCommentType(CommentType.REGION)}
            color={selectedTool === CommentType.REGION ? "primary" : "default"}
            startIcon={<FontAwesomeIcon icon={faDrawPolygon} />}
          >
            Region
          </Button>
          <Button
            width="150px"
            style={{ marginRight: 10, marginBottom: 10, minWidth: 195 }}
            variant="contained"
            onClick={() => changeCommentType(CommentType.DISTANCE)}
            color={
              selectedTool === CommentType.DISTANCE ? "primary" : "default"
            }
            startIcon={<FontAwesomeIcon icon={faRuler} />}
          >
            Distance
          </Button>
        </div>
        <div>
          <Button
            width="150px"
            style={{ marginRight: 10, marginBottom: 10, minWidth: 195 }}
            variant="contained"
            onClick={() => changeCommentType(CommentType.COMMENT_BOX)}
            color={
              selectedTool === CommentType.COMMENT_BOX ? "primary" : "default"
            }
            startIcon={<CommentIcon />}
          >
            COMMENT
          </Button>
        </div>
      </div>
      <div
        style={{
          height: "calc(100% - 138px)",
          overflow: "auto",
          maxHeight: "300px",
          marginTop: "10px",
        }}
      >
        <List component="nav">
          <LazyRender
            ref={lazyRenderRef}
            maxContainerHeight={284}
            elementHeight={96}
            offsetElements={5}
          >
            {commentRois
              .map((commentRoi, i) => (
                <ListItemButton
                  key={"listItem" + i}
                  ref={(el) => (listItemRefs.current[i] = el)}
                  selected={selectedCommentIdx === i}
                  onClick={() => {
                    changeSelectedCommentIdx(i);
                    forceUpdate();
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      {commentRoi.type === CommentType.RECTANGLE && (
                        <Crop169Icon />
                      )}
                      {commentRoi.type === CommentType.ARROW && (
                        <ArrowForwardOutlinedIcon />
                      )}
                      {commentRoi.type === CommentType.REGION && (
                        <FontAwesomeIcon icon={faDrawPolygon} />
                      )}
                      {commentRoi.type === CommentType.DISTANCE && (
                        <FontAwesomeIcon icon={faRuler} />
                      )}
                      {commentRoi.type === CommentType.COMMENT_BOX && (
                        <CommentIcon />
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <TextField
                    style={{ marginRight: 92 }}
                    label={
                      commentRoi.computedValue
                        ? commentRoi.computedValue.length > 0 &&
                          commentRoi.type !== CommentType.COMMENT_BOX
                          ? "Comment, Size: " + commentRoi.computedValue
                          : "Comment"
                        : "Comment"
                    }
                    name="Textfield"
                    fullWidth
                    multiline
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={commentRoi.commentValue}
                    onChange={(e) => {
                      changeSelectedCommentValue(i, e.target.value);
                      forceUpdate();
                    }}
                  />
                  <ListItemSecondaryAction>
                    <Grid container direction="row" alignItems="center">
                      <div>
                        <SketchColorPicker
                          color={commentRoi.color}
                          handleChange={(newColor) => {
                            changeSelectedColor(i, newColor);
                            forceUpdate();
                          }}
                        />
                      </div>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => {
                          deleteCommentRoi(i);
                          forceUpdate();
                        }}
                        size="large"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </ListItemSecondaryAction>
                </ListItemButton>
              ))
              .reverse()}
          </LazyRender>
        </List>
      </div>
    </div>
  );
};

ConfigForm.propTypes = {
  changeCommentState: PropTypes.func.isRequired,
  changeSelectedColor: PropTypes.func.isRequired,
  changeSelectedCommentIdx: PropTypes.func.isRequired,
  changeSelectedCommentValue: PropTypes.func.isRequired,
  clickedCommentIdx: PropTypes.number.isRequired,
  commentRois: PropTypes.array.isRequired,
  commentState: PropTypes.object.isRequired,
  deleteCommentRoi: PropTypes.func.isRequired,
  selectedCommentIdx: PropTypes.number.isRequired,
};

export default CommentTool;
